import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, FileText, ClipboardCheck, Receipt, Phone, Mail, CalendarDays, LockOpen } from "lucide-react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleString("cs-CZ") : "—");

const Section = ({ icon: Icon, title, count, children, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
    <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
      <Icon className="h-4 w-4 text-emerald-600" />
      <h2 className="text-sm font-bold text-slate-900">{title}</h2>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">{count}</span>
    </div>
    {children}
  </div>
);

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = () => {
    api.get(`/admin/users/${userId}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user]);

  if (user && user.role !== "admin") {
    return <Layout><p className="py-20 text-center text-sm text-slate-500">Tato sekce je dostupná pouze administrátorům.</p></Layout>;
  }
  if (!data) {
    return <Layout><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div></Layout>;
  }

  const u = data.user;
  const unlock = async (id) => {
    try {
      await api.post(`/admin/audits/${id}/unlock`);
      toast.success("Audit odemčen");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Layout>
      <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="mb-4 text-slate-600 hover:text-slate-900" data-testid="back-to-admin-button">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Zpět na administraci
      </Button>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-user-profile">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-extrabold tracking-tight text-slate-900">{u.name || u.email}</h1>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" /> {u.email}</span>
              <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" /> {u.phone || "—"}
                {u.phone_verified && <span className="text-emerald-600 font-semibold">ověřen ✓</span>}</span>
              <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-slate-400" /> registrace {fmtDate(u.created_at)}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {u.role === "admin" && <span className="rounded-full bg-slate-900 px-2.5 py-1 text-xs font-bold text-white">admin</span>}
            <span className="rounded-full bg-sky-100 px-2.5 py-1 text-xs font-bold text-sky-800">
              {u.source === "public_funnel" ? "z funnelu" : "z aplikace"}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <Section icon={ClipboardCheck} title="Audity" count={data.audits.length} testId="admin-user-audits">
          {data.audits.length === 0 ? <p className="px-5 py-4 text-sm text-slate-400">Žádné audity.</p> : (
            <div className="divide-y divide-slate-100">
              {data.audits.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{a.document_filename || "Dokument"}</div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {fmtDate(a.created_at)} · {a.status}
                      {a.summary && ` · ${a.summary.critical_count} krit. · ${a.summary.high_count} záv. · ${a.summary.medium_count} stř.`}
                      {a.paid ? " · zaplaceno" : " · náhled"}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/audits/${a.id}`)} data-testid={`user-audit-open-${a.id}`}>Otevřít</Button>
                    {!a.paid && a.status === "done" && (
                      <Button size="sm" variant="outline" onClick={() => unlock(a.id)}
                        className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid={`user-audit-unlock-${a.id}`}>
                        <LockOpen className="mr-1 h-3 w-3" /> Odemknout
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={FileText} title="Dokumenty ŠVP" count={data.documents.length} testId="admin-user-documents">
          {data.documents.length === 0 ? <p className="px-5 py-4 text-sm text-slate-400">Žádné dokumenty.</p> : (
            <div className="divide-y divide-slate-100">
              {data.documents.map((d) => (
                <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{d.filename}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{d.file_type} · {d.page_count} stran · {fmtDate(d.created_at)}</div>
                  </div>
                  <span className="font-mono text-[10px] text-slate-400">{(d.sha256 || "").slice(0, 12)}…</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section icon={Receipt} title="Platby a faktury" count={data.purchases.length} testId="admin-user-purchases">
          {data.purchases.length === 0 ? <p className="px-5 py-4 text-sm text-slate-400">Žádné platby.</p> : (
            <div className="divide-y divide-slate-100">
              {data.purchases.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">
                      Faktura {p.invoice?.number || "—"}
                      {p.invoice?.mock && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">DEV</span>}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {p.billing?.name} · {p.invoice ? `${p.invoice.amount?.toLocaleString("cs-CZ")} Kč` : ""} · stav: {p.status}
                      {p.paid_at && ` · zaplaceno ${fmtDate(p.paid_at)}`}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2 text-xs">
                    {p.invoice?.public_url && <a href={p.invoice.public_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline">online</a>}
                    {p.invoice?.pdf_url && <a href={p.invoice.pdf_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline">PDF</a>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </Layout>
  );
}
